import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  FaSearch, FaSync, FaFilter, FaTimes, FaEye,
  FaTags, FaChevronDown, FaUser,
  FaCreditCard, FaFileInvoice, FaGlobe, FaMoneyBillWave,
  FaCheckCircle, FaHourglassHalf, FaTimesCircle, FaUserCircle,
  FaPlus, FaSpinner, FaRupeeSign
} from "react-icons/fa";
import Navbar from "./Navbar";
import Footer from "./Footer";

/* ─────────────────────────────────────────────────────
   TOAST
───────────────────────────────────────────────────── */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 5000);
    return () => clearTimeout(t);
  }, [onClose]);

  const cfg = {
    success: { icon: <FaCheckCircle />, label: "Success", accent: "#2a6b2a", iconBg: "rgba(42,107,42,0.12)", border: "rgba(42,107,42,0.3)", symbol: "✦" },
    error:   { icon: <FaTimes />,       label: "Error",   accent: "#8b1a1a", iconBg: "rgba(139,26,26,0.10)", border: "rgba(139,26,26,0.28)", symbol: "✗" },
    info:    { icon: <FaSearch />,      label: "Notice",  accent: "#8a6020", iconBg: "rgba(201,151,58,0.10)", border: "rgba(201,151,58,0.30)", symbol: "ॐ" },
  };
  const c = cfg[type] || cfg.info;

  return (
    <div className="mb-toast-wrap">
      <div className="mb-toast" style={{ "--accent": c.accent, "--border": c.border }}>
        <div className="mb-toast-bar" />
        <span className="mb-tc tl" /><span className="mb-tc tr" />
        <span className="mb-tc bl" /><span className="mb-tc br" />
        <div className="mb-toast-medal" style={{ background: c.iconBg, borderColor: c.border }}>
          <div className="mb-toast-spin" />
          <span style={{ color: c.accent, fontSize: 14, position: "relative", zIndex: 1 }}>{c.icon}</span>
        </div>
        <div className="mb-toast-body">
          <div className="mb-toast-row">
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
            <span className="mb-toast-title" style={{ color: c.accent }}>{c.label}</span>
            <span style={{ color: c.accent, fontSize: 9 }}>{c.symbol}</span>
          </div>
          <div className="mb-toast-rule" style={{ background: `linear-gradient(90deg,transparent,${c.accent}55,transparent)` }} />
          <p className="mb-toast-msg">{message}</p>
        </div>
        <button className="mb-toast-x" onClick={onClose} style={{ color: c.accent }}>✕</button>
        <div className="mb-toast-foot" />
      </div>
    </div>
  );
}


/* ─────────────────────────────────────────────────────
   BOOKING MODAL — Aofferings regal style (no date/phone)
───────────────────────────────────────────────────── */
function BookingFormModal({ category, onClose, onConfirm, processing }) {
  const { t } = useTranslation();
  const [persons, setPersons] = useState([{ name: "", nakshathram: "" }]);

  const nakshatrams = [
    "Aswathy / അശ്വതി", "Bharani / ഭരണി", "Karthika / കാർത്തിക",
    "Rohini / രോഹിണി", "Makayiram / മകയിരം", "Thiruvathira / തിരുവാതിര",
    "Punartham / പുണർതം", "Pooyam / പൂയം", "Ayilyam / ആയില്യം",
    "Makam / മകം", "Pooram / പൂരം", "Uthram / ഉത്രം",
    "Atham / അത്തം", "Chithira / ചിത്തിര", "Chothi / ചോതി",
    "Vishakham / വിശാഖം", "Anizham / അനിഴം", "Thrikketta / തൃക്കേട്ട",
    "Moolam / മൂലം", "Pooradam / പൂരാടം", "Uthradam / ഉത്രാടം",
    "Thiruvonam / തിരുവോണം", "Avittam / അവിട്ടം", "Chathayam / ചതയം",
    "Pooruruttathi / പൂരുരുട്ടാതി", "Uthrattathi / ഉത്രട്ടാതി", "Revathi / രേവതി"
  ];

  const handlePersonChange = (index, field, value) => {
    const updated = [...persons];
    updated[index][field] = value;
    setPersons(updated);
  };

  const addPerson = () => setPersons([...persons, { name: "", nakshathram: "" }]);

  const removePerson = (index) => {
    if (persons.length > 1) setPersons(persons.filter((_, i) => i !== index));
  };

  const handleSubmit = () => {
    for (let p of persons) {
      if (!p.name.trim()) { alert("Please enter name for all devotees"); return; }
    }
    const today = new Date().toISOString().split("T")[0]; // "YYYY-MM-DD"
    onConfirm({ bookingDate: today, persons });
  };

  if (!category) return null;

  return createPortal(
    <div className="adv-modal-overlay" onClick={onClose}>
      <div className="adv-regal-modal adv-edit-regal" onClick={e => e.stopPropagation()}>
        <div className="adv-rm-topglow" />
        <span className="adv-cm-corner tl" /><span className="adv-cm-corner tr" />
        <span className="adv-cm-corner bl" /><span className="adv-cm-corner br" />

        {/* Header */}
        <div className="adv-rm-header">
          <div className="adv-rm-header-bg" />
          <div className="adv-rm-header-inner">
            <div className="adv-rm-header-icon-wrap">
              <div className="adv-rm-hicon-ring" />
              <FaTags className="adv-rm-hicon" />
            </div>
            <div className="adv-rm-header-eyebrow">✦ {t("bookNow") || "Book Now"} ✦</div>
            <h2 className="adv-rm-title">{t("bkgModTitle") || "Vazhipadu Booking"}</h2>
            <p className="adv-rm-name-tag">{category.name}</p>
          </div>
          <button className="adv-rm-close" onClick={onClose}><FaTimes /></button>
        </div>
        <div className="adv-rm-rule"><span className="adv-rm-rule-ornament">❖</span></div>

        {/* Body */}
        <div className="adv-edit-body">
          {/* Amount */}
          <div className="adv-edit-section-label">✦ {t("bkgSecBooking") || "Offering"}</div>
          <div className="adv-edit-grid" style={{ marginBottom: 20 }}>
            <div className="adv-edit-field full-width">
              <label className="adv-edit-label">{t("bkgLblAmount") || "Total Amount"}</label>
              <div className="adv-edit-wrap adv-edit-wrap-readonly">
                <span className="adv-edit-prefix"><FaRupeeSign /></span>
                <span className="adv-edit-input adv-readonly-val">
                  {(parseFloat(category.amount) * persons.length).toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          {/* Devotee rows */}
          <div className="adv-edit-section-label">✦ {t("bkgSecDevotee") || "Devotee Details"}</div>

          {persons.map((person, index) => (
            <div key={index} className="adv-person-block">
              <div className="adv-person-block-header">
                <span className="adv-person-block-title">{t("bkgPerson") || "Person"} {index + 1}</span>
                {index > 0 && (
                  <button className="adv-person-remove" onClick={() => removePerson(index)}>
                    <FaTimes /> {t("bkgBtnRemove") || "Remove"}
                  </button>
                )}
              </div>
              <div className="adv-edit-grid">
                <div className="adv-edit-field">
                  <label className="adv-edit-label">{t("bkgLblName") || "Name"} <span className="adv-req">*</span></label>
                  <div className="adv-edit-wrap">
                    <input
                      type="text" className="adv-edit-input"
                      placeholder={t("bkgPhName") || "Devotee name"}
                      value={person.name}
                      onChange={e => handlePersonChange(index, "name", e.target.value)}
                    />
                  </div>
                </div>
                <div className="adv-edit-field">
                  <label className="adv-edit-label">{t("bkgLblNakshathram") || "Nakshathram"}</label>
                  <div className="adv-edit-wrap adv-select-wrap">
                    <select
                      className="adv-edit-input adv-nakshathram-select"
                      value={person.nakshathram}
                      onChange={e => handlePersonChange(index, "nakshathram", e.target.value)}
                    >
                      <option value="">— നക്ഷത്രം തിരഞ്ഞെടുക്കുക —</option>
                      {nakshatrams.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                    <span className="adv-select-arrow">▾</span>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <button className="adv-add-person-btn" onClick={addPerson}>
            <FaPlus /> {t("bkgBtnAddPerson") || "Add Person"}
          </button>

          <div className="adv-edit-actions" style={{ justifyContent: "center" }}>
            <button className="adv-btn adv-btn-ghost" onClick={onClose} disabled={processing}>
              {t("btnCancel") || "Cancel"}
            </button>
            <button className="adv-edit-save-btn" onClick={handleSubmit} disabled={processing}>
              {processing
                ? <><span className="adv-edit-saving-spinner" />{t("bkgRegistering") || "Processing"}…</>
                : <><FaRupeeSign /> {`Pay \u20b9${(parseFloat(category.amount) * persons.length).toLocaleString("en-IN")}`}</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

/* ─────────────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────────────── */
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

export default function Bookings() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // --- AUTH ---
  const [user, setUser] = useState(null);
  
  // --- DATA ---
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- UI ---
  const [toast, setToast] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null); // For Modal
  const [processing, setProcessing] = useState(false);
  const [logoFace, setLogoFace] = useState("om");

  const razorpayScriptRef = useRef(null);

  // Helper
  const showToast = (message, type = "info") => setToast({ message, type });

  // 1. Effects
  useEffect(() => {
    const timer = setInterval(() => setLogoFace(f => f === "om" ? "logo" : "om"), 3000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    // Load Razorpay
    if (!window.Razorpay) {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;
      script.onload = () => { razorpayScriptRef.current = true; };
      document.body.appendChild(script);
    } else {
      razorpayScriptRef.current = true;
    }
  }, []);

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    const token = localStorage.getItem("authToken");
    if (!storedUser || !token) { navigate("/login"); return; }
    try { setUser(JSON.parse(storedUser)); } catch { navigate("/login"); }

    // Fetch Categories
    const fetchCats = async () => {
      try {
        const res = await fetch(`${API}/api/vazhipadu-categories`);
        const data = await res.json();
        setCategories(data.filter(c => c.active !== false));
      } catch (err) { showToast("Failed to load categories", "error"); }
      finally { setLoading(false); }
    };
    fetchCats();
  }, [API, navigate]);

  // 2. Handlers
  const handleBookingConfirm = async ({ bookingDate, persons }) => {
    if (!user?.mobile) return;
    setProcessing(true);

    try {
      const amount = parseFloat(selectedCategory.amount) * persons.length * 100; // paise (per person × count)

      // Step A: Create Order
      const orderRes = await fetch(`${API}/api/create-booking-order`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("authToken")}` },
        body: JSON.stringify({
          amount, currency: "INR", receipt: `rcpt_${Date.now()}`
        })
      });
      if (!orderRes.ok) throw new Error("Order Failed");
      const orderData = await orderRes.json();

      // Step B: Open Razorpay
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID, // Ensure this is in .env
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Kodungallur Kavil Sree Bhadrakali Devi Temple",
        description: selectedCategory.name,
        order_id: orderData.id,
        handler: async (response) => {
          await verifyAndSave(response, orderData, bookingDate, persons);
        },
        prefill: { name: user.name, email: user.email, contact: user.mobile },
        theme: { color: "#c9973a" },
        modal: { ondismiss: () => { setProcessing(false); showToast("Payment Cancelled", "info"); } }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error(err);
      showToast(err.message, "error");
      setProcessing(false);
    }
  };

  const verifyAndSave = async (rzpResp, orderData, bookingDate, persons) => {
    try {
      const res = await fetch(`${API}/api/verify-booking-payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${localStorage.getItem("authToken")}` },
        body: JSON.stringify({
          razorpay_order_id: orderData.id,
          razorpay_payment_id: rzpResp.razorpay_payment_id,
          razorpay_signature: rzpResp.razorpay_signature,
          vazhipadu_id: selectedCategory.vazhipadu_id,
          booking_date: bookingDate,
          total_persons: persons.length,
          persons: persons,
          amount: selectedCategory.amount
        })
      });
      const data = await res.json();
      if (res.ok) {
        showToast("Booking Successful!", "success");
        setSelectedCategory(null);
        setTimeout(() => navigate("/Mybooking"), 2000);
      } else {
        throw new Error(data.error || "Verification Failed");
      }
    } catch (err) {
      showToast(err.message, "error");
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return (
    <>
      <MbStyles />
      <div className="bk-splash">
        <div className="bk-splash-bg" />
        <div className="bk-splash-geo" />
        <div className="bk-mandala">
          {[160, 124, 92, 62].map((s, i) => (
            <div key={i} className="bk-mring" style={{ width: s, height: s, animationDelay: `${i * 0.3}s`, animationDuration: `${6 + i * 3}s`, animationDirection: i % 2 === 0 ? 'normal' : 'reverse' }} />
          ))}
          <div className="bk-mandala-halo" />
          <div className="bk-mandala-om">ॐ</div>
        </div>
        <div className="bk-splash-dots">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="bk-dot" style={{ animationDelay: `${i * 0.18}s`, transform: `rotate(${i * 45}deg) translateY(-54px)` }} />
          ))}
        </div>
        <div className="bk-splash-text">
          <div className="bk-splash-eyebrow">✦ Kavil Temple ✦</div>
          <p className="bk-splash-label">Loading offerings…</p>
        </div>
      </div>
    </>
  );

  return (
    <>
      <MbStyles />
      <Navbar />

      {/* HERO */}
      <div className="mb-hero">
        <div className="mb-hero-bg" />
        <div className="mb-hero-rings">
          {[220, 170, 125, 85].map((s, i) => (
            <div key={i} className="mb-hero-ring" style={{ width: s, height: s, animationDelay: `${i * 0.5}s` }} />
          ))}
        </div>

        {/* Left side column */}
        <div className="mb-hero-side-col mb-side-left">
          <div className="mb-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="mb-hlc-om">ॐ</div>
          <div className="mb-hlc-line" />
          <span className="mb-hlc-symbol">◈</span>
          <div className="mb-hlc-line" />
          <div className="mb-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

        {/* Center */}
        <div className="mb-hero-center">
          <div className="mb-hero-medallion">
            <div className="mb-hero-med-ring" />
            <div className="mb-hero-med-ring mb-hero-med-ring-2" />
            <div className={`mb-hero-face ${logoFace === "om" ? "visible" : ""}`}>
              <span className="mb-hero-om">ॐ</span>
            </div>
            <div className={`mb-hero-face ${logoFace === "logo" ? "visible" : ""}`}>
              <img src="./logo.png" alt="Temple" className="mb-hero-logo-img" />
            </div>
            <div className="mb-hero-med-halo" />
          </div>

          <div className="mb-hero-text">
           <div className="mb-hero-eyebrow">✦ {t("templeName") || "Devotee Portal"} ✦</div>
            <h1 className="mb-hero-title">{t("bookVazhipaduTitle") || "Book Vazhipadu"}</h1>
            <p className="mb-hero-sub">{t("bookVazhipaduSub") || "Select an offering to begin your booking"}</p>
            <button className="mb-my-bookings-btn" onClick={() => navigate("/Mybooking")}>
              <FaFileInvoice /> {t("myBookingsBtn") || "My Bookings"}
            </button>
          </div>

          <div className="mb-hero-rule" />
        </div>

        {/* Right side column */}
        <div className="mb-hero-side-col mb-side-right">
          <div className="mb-hlc-top-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.15 + i * 0.1, height: 3 + i * 2 }} />
            ))}
          </div>
          <div className="mb-hlc-om">ॐ</div>
          <div className="mb-hlc-line" />
          <span className="mb-hlc-symbol">◈</span>
          <div className="mb-hlc-line" />
          <div className="mb-hlc-bot-ornament">
            {[...Array(7)].map((_, i) => (
              <div key={i} className="mb-hlc-tick" style={{ opacity: 0.85 - i * 0.1, height: 3 + (6 - i) * 2 }} />
            ))}
          </div>
        </div>

      </div>


      {/* CONTENT */}
      <div className="adv-offerings-wrap">
        {loading ? (
          <div className="mb-loader">
            <div className="mb-loader-ring-wrap">
              <div className="mb-loader-ring" />
              <div className="mb-loader-ring-inner" />
              <div className="mb-loader-om">ॐ</div>
            </div>
            <p>Loading...</p>
          </div>
        ) : categories.length === 0 ? (
          <div className="adv-empty">
            <div className="adv-empty-om">ॐ</div>
            <h3 className="adv-empty-title">{t("bkgEmptyTitle") || "No Offerings Available"}</h3>
            <p className="adv-empty-sub">{t("bkgEmptySub") || "Please check back later"}</p>
          </div>
        ) : (
          <div className="adv-vazhipadu-grid">
            {categories.map((cat, i) => (
              <div
                key={cat.vazhipadu_id}
                className="adv-vazhipadu-card"
                style={{ animationDelay: `${i * 0.07}s` }}
                onClick={() => setSelectedCategory(cat)}
              >
                {/* Icon medallion */}
                <div className="adv-vz-medallion">
                  <div className="adv-vz-med-ring" />
                  <div className="adv-hero-face visible">
                    <span className="adv-vz-om">ॐ</span>
                  </div>
                </div>

                <div className="adv-vz-name">{cat.name}</div>

                {cat.description && (
                  <p className="adv-vz-desc">{cat.description}</p>
                )}

                <div className="adv-vz-footer">
                  <div className="adv-vz-price">
                    <FaRupeeSign className="adv-vz-rupee" />
                    <span>{parseFloat(cat.amount).toFixed(2)}</span>
                    <span className="adv-vz-per">/ {t("bkgPerPerson") || "per person"}</span>
                  </div>
                  <button className="adv-vz-book-btn" onClick={e => { e.stopPropagation(); setSelectedCategory(cat); }}>
                    <FaTags /> {t("bookNow") || "Book Now"}
                  </button>
                </div>

                <span className="adv-vz-corner vz-tl" />
                <span className="adv-vz-corner vz-br" />
              </div>
            ))}
          </div>
        )}
      </div>

      {selectedCategory && (
        <BookingFormModal 
          category={selectedCategory} 
          onClose={() => setSelectedCategory(null)} 
          onConfirm={handleBookingConfirm}
          processing={processing}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      <Footer />
    </>
  );

/* ═══════════════════════════════════════════════════
   STYLES
═══════════════════════════════════════════════════ */
function MbStyles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Cinzel+Decorative:wght@400;700;900&family=Cinzel:wght@400;500;600;700&family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap');

      :root {
        --gold-bright: #e8c46a; --gold-mid: #c9973a; --gold-dim: #8a6020;
        --crimson-deep: #1a0404; --crimson-mid: #2c0808; --crimson-rich: #3d0c0c;
        --hero-1: #2e0c0c; --hero-2: #1c0505; --hero-3: #100202;
        --parchment: #FDF3E3; --parchment-dark: #f5e8cc;
        --text-dark: #1a0404; --text-mid: #4a1a08;
      }
      *, *::before, *::after { margin: 0; padding: 0; box-sizing: border-box; }

      /* ══ HERO ══ */
      .mb-hero { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1) 0%, var(--hero-2) 50%, var(--hero-3) 100%); padding: 40px 32px 0; display: flex; flex-direction: column; align-items: center; }
      .mb-hero-bg { position: absolute; inset: 0; pointer-events: none; background: radial-gradient(ellipse at 15% 30%, rgba(160,35,35,0.35) 0%, transparent 50%), radial-gradient(ellipse at 85% 70%, rgba(80,15,15,0.3) 0%, transparent 45%); }
      .mb-hero-rings { position: absolute; top: 30px; right: 60px; width: 230px; height: 230px; pointer-events: none; }
      .mb-hero-ring { position: absolute; top: 50%; left: 50%; transform: translate(-50%,-50%); border-radius: 50%; border: 1px solid rgba(232,196,106,0.07); animation: mbRingPulse 4s ease-in-out infinite alternate; }
      .mb-hero-ring:nth-child(odd) { border-style: dashed; }
      @keyframes mbRingPulse { from { opacity: 0.3; } to { opacity: 0.8; } }
      .mb-hero-medallion { width: 90px; height: 90px; border-radius: 50%; background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%); border: 1px solid rgba(232,196,106,0.3); display: flex; align-items: center; justify-content: center; position: relative; margin-bottom: 20px; box-shadow: 0 0 30px rgba(232,196,106,0.15), inset 0 1px 0 rgba(255,255,255,0.06); }
      .mb-hero-med-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: mbSlowSpin 10s linear infinite; }
      .mb-hero-med-ring-2 { inset: -10px; border-style: dotted; border-color: rgba(232,196,106,0.12); animation-duration: 18s; animation-direction: reverse; }
      @keyframes mbSlowSpin { to { transform: rotate(360deg); } }
      .mb-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transform: scale(0.7) rotateY(90deg); transition: opacity 0.55s ease, transform 0.55s ease; }
      .mb-hero-face.visible { opacity: 1; transform: scale(1) rotateY(0deg); }
      .mb-hero-om { font-family: "Cinzel Decorative", cursive; font-size: 34px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); }
      .mb-hero-logo-img { width: 54px; height: 54px; object-fit: contain; filter: drop-shadow(0 0 8px rgba(232,196,106,0.4)); }
      .mb-hero-med-halo { position: absolute; inset: -16px; border-radius: 50%; background: radial-gradient(circle, rgba(232,196,106,0.08) 0%, transparent 65%); pointer-events: none; }
      .mb-hero-text { text-align: center; position: relative; z-index: 1; }
      .mb-hero-eyebrow { font-family: "Cinzel", serif; font-size: 8.5px; letter-spacing: 2px; color: rgba(232,196,106,0.35); text-transform: uppercase; margin-bottom: 10px; }
      .mb-hero-title { font-family: "Cinzel Decorative", cursive; font-size: 28px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.2; margin-bottom: 8px; }
      .mb-hero-sub { font-family: "EB Garamond", serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.4); margin-bottom: 14px; letter-spacing: 0.3px; }
      .mb-my-bookings-btn { display: inline-flex; align-items: center; gap: 7px; margin-bottom: 28px; font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 45%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 11px 24px; cursor: pointer; clip-path: polygon(12px 0%, calc(100% - 12px) 0%, 100% 50%, calc(100% - 12px) 100%, 12px 100%, 0% 50%); box-shadow: 0 4px 18px rgba(201,151,58,0.28); transition: all 0.3s ease; }
      .mb-my-bookings-btn:hover { transform: translateY(-2px); box-shadow: 0 8px 28px rgba(201,151,58,0.48); filter: brightness(1.07); }
      .mb-hero-rule { width: 60%; max-width: 400px; height: 1px; margin: 0 auto; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); }

      /* ══ HERO LAYOUT: 3-column grid ══ */
      .mb-hero { display: grid !important; grid-template-columns: 72px 1fr 72px; align-items: stretch; padding: 0 !important; }
      .mb-hero-center { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 40px 32px 0; position: relative; z-index: 2; }
      .mb-hero-side-col { display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; padding: 28px 0; position: relative; z-index: 2; }
      .mb-side-left { border-right: 1px solid rgba(201,151,58,0.12); }
      .mb-side-right { border-left: 1px solid rgba(201,151,58,0.12); }
      .mb-hlc-om { font-family: "Cinzel Decorative", cursive; font-size: 20px; background: linear-gradient(180deg, #fff8e7, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; filter: drop-shadow(0 0 8px rgba(232,196,106,0.5)); line-height: 1; }
      .mb-hlc-line { width: 1px; flex: 1; min-height: 16px; background: linear-gradient(180deg, transparent, rgba(201,151,58,0.3), transparent); }
      .mb-hlc-symbol { font-size: 16px; color: rgba(232,196,106,0.5); line-height: 1; }
      .mb-hlc-tick { width: 2px; border-radius: 1px; background: linear-gradient(180deg, var(--gold-bright), var(--gold-mid)); }
      .mb-hlc-top-ornament, .mb-hlc-bot-ornament { display: flex; flex-direction: column; align-items: center; gap: 3px; }
      @media (max-width: 640px) {
        .mb-hero { grid-template-columns: 32px 1fr 32px; }
        .mb-hlc-om { font-size: 14px; }
        .mb-hlc-symbol { font-size: 11px; }
      }

      /* ══ OFFERINGS GRID ══ */
      .adv-offerings-wrap { padding: 36px 28px 60px; background: #f8ead4; }
      .adv-vazhipadu-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 20px;
        max-width: 1100px;
        margin: 0 auto;
      }
      .adv-vazhipadu-card {
        position: relative;
        padding: 28px 22px;
        border: 1px solid rgba(232,196,106,0.1);
        background: #0e0101;
        overflow: hidden;
        clip-path: polygon(0 0, calc(100% - 18px) 0, 100% 18px, 100% 100%, 18px 100%, 0 calc(100% - 18px));
        cursor: pointer;
        transition: all 0.35s ease;
        animation: rowIn 0.4s ease both;
        display: flex; flex-direction: column; gap: 8px;
      }
      @keyframes rowIn { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      .adv-vazhipadu-card::before {
        content: ""; position: absolute; top: 0; left: 0;
        width: 3px; height: 0;
        background: linear-gradient(180deg, #e8c46a, #c9973a);
        transition: height 0.35s ease;
      }
      .adv-vazhipadu-card:hover::before { height: 100%; }
      .adv-vazhipadu-card:hover { border-color: rgba(232,196,106,0.22); transform: translateY(-3px); box-shadow: 0 14px 36px rgba(0,0,0,0.4); }

      .adv-vz-medallion {
        width: 54px; height: 54px; border-radius: 50%;
        background: radial-gradient(circle at 40% 35%, #5a1515 0%, #2a0707 55%, #0e0101 100%);
        border: 1px solid rgba(232,196,106,0.35);
        display: flex; align-items: center; justify-content: center;
        position: relative; box-shadow: 0 0 20px rgba(232,196,106,0.1);
        flex-shrink: 0; margin-bottom: 4px; align-self: center;
      }
      .adv-vz-med-ring { position: absolute; inset: -4px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.25); animation: slowSpin 12s linear infinite; }
      @keyframes slowSpin { to { transform: rotate(360deg); } }
      .adv-hero-face { position: absolute; inset: 0; border-radius: 50%; display: flex; align-items: center; justify-content: center; opacity: 0; transition: opacity 0.55s ease; }
      .adv-hero-face.visible { opacity: 1; }
      .adv-vz-om {
        font-family: "Cinzel Decorative", cursive; font-size: 18px;
        background: linear-gradient(180deg, #fff8e7, #e8c46a, #c9973a);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 0 6px rgba(232,196,106,0.4));
        position: relative; z-index: 1;
      }
      .adv-vz-name {
        font-family: "Cinzel", serif; font-size: 18px; font-weight: bold;
        letter-spacing: 1.5px; text-transform: uppercase;
        background: linear-gradient(135deg, #fffaf0, #e8c46a);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        margin-bottom: 4px; text-align: center;
      }
      .adv-vz-desc { font-family: "EB Garamond", serif; font-size: 14px; color: rgba(245,228,176,0.5); line-height: 1.6; flex: 1; margin: 0; text-align: center; }
      .adv-vz-footer { width: 100%; display: flex; justify-content: space-between; align-items: center; margin-top: 8px; padding-top: 14px; border-top: 1px solid rgba(232,196,106,0.1); gap: 8px; flex-wrap: wrap; }
      .adv-vz-price { display: flex; align-items: baseline; gap: 3px; font-family: "Cinzel Decorative", cursive; font-size: 16px; font-weight: bold; color: #c9973a; }
      .adv-vz-rupee { font-size: 11px; color: #c9973a; }
      .adv-vz-per { font-family: "EB Garamond", serif; font-style: italic; font-size: 11px; color: rgba(245,228,176,0.35); font-weight: 400; }
      .adv-vz-book-btn {
        display: inline-flex; align-items: center; gap: 6px;
        font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
        color: #1a0404; background: linear-gradient(135deg, #c9973a, #e8c46a, #c9973a);
        border: none; padding: 7px 16px; cursor: pointer;
        clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 50%, calc(100% - 8px) 100%, 8px 100%, 0% 50%);
        transition: all 0.3s ease;
      }
      .adv-vz-book-btn:hover { filter: brightness(1.1); transform: scale(1.05); }
      .adv-vz-corner { position: absolute; width: 12px; height: 12px; border-color: rgba(201,151,58,0.35); border-style: solid; }
      .adv-vz-corner.vz-tl { top: 8px; left: 8px; border-width: 1.5px 0 0 1.5px; }
      .adv-vz-corner.vz-br { bottom: 8px; right: 8px; border-width: 0 1.5px 1.5px 0; }

      /* ══ EMPTY ══ */
      .adv-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 70px 0; gap: 14px; text-align: center; }
      .adv-empty-om { font-family: "Cinzel Decorative", cursive; font-size: 52px; background: linear-gradient(180deg, #e8c46a, #c9973a); -webkit-background-clip: text; -webkit-text-fill-color: transparent; opacity: 0.25; }
      .adv-empty-title { font-family: "Cinzel Decorative", cursive; font-size: 20px; color: rgba(245,228,176,0.6); }
      .adv-empty-sub { font-family: "EB Garamond", serif; font-style: italic; font-size: 15px; color: rgba(245,228,176,0.35); max-width: 320px; }

      /* ══ FULL-PAGE SPLASH LOADER ══ */
      .bk-splash {
        position: fixed; inset: 0; z-index: 999;
        display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 28px;
        background: linear-gradient(165deg, #0e0101 0%, #1a0404 30%, #2c0808 55%, #1a0404 78%, #0a0000 100%);
        animation: bkFadeIn 0.5s ease;
      }
      @keyframes bkFadeIn { from { opacity: 0; } to { opacity: 1; } }
      .bk-splash-bg {
        position: absolute; inset: 0; pointer-events: none;
        background:
          radial-gradient(ellipse 60% 50% at 50% 30%, rgba(100,18,18,0.45) 0%, transparent 65%),
          repeating-linear-gradient(-55deg, transparent 0px, transparent 38px, rgba(201,151,58,0.015) 38px, rgba(201,151,58,0.015) 39px);
      }
      .bk-splash-geo {
        position: absolute; inset: 0; pointer-events: none;
        background:
          radial-gradient(ellipse 30% 80% at 0% 50%, rgba(201,151,58,0.04) 0%, transparent 100%),
          radial-gradient(ellipse 30% 80% at 100% 50%, rgba(201,151,58,0.04) 0%, transparent 100%);
      }
      /* ── Mandala ── */
      .bk-mandala {
        position: relative; width: 160px; height: 160px;
        display: flex; align-items: center; justify-content: center;
      }
      .bk-mring {
        position: absolute; border-radius: 50%;
        border: 1px solid rgba(201,151,58,0.22);
        animation: bkSpin 8s linear infinite;
      }
      .bk-mring:nth-child(1) { border-style: dashed; border-color: rgba(232,196,106,0.18); }
      .bk-mring:nth-child(2) { border-color: rgba(201,151,58,0.35); border-style: dotted; }
      .bk-mring:nth-child(3) { border-color: rgba(160,106,21,0.5); }
      .bk-mring:nth-child(4) { border-width: 1.5px; border-color: rgba(232,196,106,0.7); border-style: dashed; }
      @keyframes bkSpin { to { transform: rotate(360deg); } }
      .bk-mandala-halo {
        position: absolute; inset: 20%; border-radius: 50%;
        background: radial-gradient(circle, rgba(201,151,58,0.18) 0%, transparent 70%);
        animation: bkHalo 2.5s ease-in-out infinite alternate;
      }
      @keyframes bkHalo { from { opacity: 0.4; transform: scale(0.9); } to { opacity: 1; transform: scale(1.15); } }
      .bk-mandala-om {
        position: relative; z-index: 2;
        font-family: 'Cinzel Decorative', cursive; font-size: 52px;
        background: linear-gradient(135deg, #fffaf0 0%, #e8c46a 35%, #c9973a 65%, #a06a15 100%);
        -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        filter: drop-shadow(0 0 16px rgba(201,151,58,0.6)) drop-shadow(0 0 32px rgba(139,26,26,0.3));
        animation: bkOmPulse 1.8s ease-in-out infinite alternate;
      }
      @keyframes bkOmPulse { from { opacity: 0.6; filter: drop-shadow(0 0 8px rgba(201,151,58,0.3)); } to { opacity: 1; filter: drop-shadow(0 0 24px rgba(201,151,58,0.8)) drop-shadow(0 0 48px rgba(139,26,26,0.4)); } }
      /* ── Floating dots ── */
      .bk-splash-dots {
        position: absolute;
        width: 160px; height: 160px;
        animation: bkSpin 12s linear infinite;
      }
      .bk-dot {
        position: absolute; top: 50%; left: 50%;
        width: 5px; height: 5px; border-radius: 50%;
        background: radial-gradient(circle, #e8c46a, #c9973a);
        margin: -2.5px 0 0 -2.5px;
        box-shadow: 0 0 6px rgba(232,196,106,0.7);
        animation: bkDotPulse 1.8s ease-in-out infinite alternate;
      }
      @keyframes bkDotPulse { from { opacity: 0.2; transform: rotate(var(--r,0deg)) translateY(-54px) scale(0.6); } to { opacity: 1; transform: rotate(var(--r,0deg)) translateY(-54px) scale(1.2); } }
      /* ── Text ── */
      .bk-splash-text { display: flex; flex-direction: column; align-items: center; gap: 6px; position: relative; z-index: 2; }
      .bk-splash-eyebrow {
        font-family: 'Cinzel', serif; font-size: 8px; letter-spacing: 4px; text-transform: uppercase;
        color: rgba(232,196,106,0.35);
        animation: bkTextFade 1.8s ease-in-out infinite alternate;
      }
      .bk-splash-label {
        font-family: 'EB Garamond', serif; font-style: italic; font-size: 17px;
        color: rgba(245,228,176,0.5); letter-spacing: 1px;
        animation: bkTextFade 1.8s ease-in-out infinite alternate 0.6s;
      }
      @keyframes bkTextFade { from { opacity: 0.3; } to { opacity: 0.85; } }

      /* ══ MODAL OVERLAY ══ */
      .adv-modal-overlay { position: fixed; inset: 0; z-index: 1000; background: rgba(8,1,1,0.85); backdrop-filter: blur(7px); display: flex; align-items: center; justify-content: center; padding: 20px; animation: overlayIn 0.2s ease; }
      @keyframes overlayIn { from { opacity: 0; } to { opacity: 1; } }

      /* ══ REGAL MODAL ══ */
      .adv-regal-modal { position: relative; width: 100%; background: linear-gradient(160deg, #FDF3E3 0%, #faf0dc 55%, #f5e8cc 100%); border: 1px solid rgba(201,151,58,0.35); box-shadow: 0 36px 110px rgba(26,4,4,0.7), 0 0 0 1px rgba(201,151,58,0.06) inset; animation: regalIn 0.35s cubic-bezier(0.23,1,0.32,1) both; overflow: hidden; }
      @keyframes regalIn { from { opacity: 0; transform: scale(0.93) translateY(26px); } to { opacity: 1; transform: scale(1) translateY(0); } }
      .adv-edit-regal { max-width: 580px; max-height: 92vh; overflow-y: auto; }
      .adv-edit-regal::-webkit-scrollbar { width: 6px; }
      .adv-edit-regal::-webkit-scrollbar-thumb { background: linear-gradient(180deg, #1a0404, #5a1515, var(--gold-mid)); border-radius: 10px; }
      .adv-rm-topglow { position: absolute; top: 0; left: 0; right: 0; height: 3px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.2) 20%, rgba(232,196,106,0.9) 50%, rgba(201,151,58,0.2) 80%, transparent); z-index: 10; }
      .adv-cm-corner { position: absolute; width: 16px; height: 16px; border-color: rgba(201,151,58,0.5); border-style: solid; z-index: 10; }
      .adv-cm-corner.tl { top: 14px; left: 14px; border-width: 1.5px 0 0 1.5px; }
      .adv-cm-corner.tr { top: 14px; right: 14px; border-width: 1.5px 1.5px 0 0; }
      .adv-cm-corner.bl { bottom: 14px; left: 14px; border-width: 0 0 1.5px 1.5px; }
      .adv-cm-corner.br { bottom: 14px; right: 14px; border-width: 0 1.5px 1.5px 0; }
      .adv-rm-header { position: relative; overflow: hidden; background: linear-gradient(160deg, var(--hero-1), var(--hero-2) 55%, var(--hero-3)); padding: 38px 28px 28px; text-align: center; border-bottom: 1px solid rgba(201,151,58,0.2); }
      .adv-rm-header-bg { position: absolute; inset: 0; background: radial-gradient(ellipse at 50% 40%, rgba(150,30,30,0.28) 0%, transparent 65%); pointer-events: none; }
      .adv-rm-header-inner { position: relative; z-index: 1; display: flex; flex-direction: column; align-items: center; gap: 8px; }
      .adv-rm-close { position: absolute; top: 16px; right: 16px; z-index: 5; background: rgba(255,255,255,0.06); border: 1px solid rgba(232,196,106,0.2); color: rgba(232,196,106,0.55); border-radius: 50%; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 11px; transition: all 0.2s; }
      .adv-rm-close:hover { background: rgba(255,255,255,0.12); color: var(--gold-bright); transform: rotate(90deg); }
      .adv-rm-header-icon-wrap { position: relative; width: 72px; height: 72px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: rgba(232,196,106,0.08); border: 1px solid rgba(232,196,106,0.2); }
      .adv-rm-hicon-ring { position: absolute; inset: -5px; border-radius: 50%; border: 1px dashed rgba(232,196,106,0.28); animation: slowSpin 9s linear infinite; }
      .adv-rm-hicon { font-size: 28px; color: var(--gold-bright); filter: drop-shadow(0 0 10px rgba(232,196,106,0.4)); position: relative; z-index: 1; }
      .adv-rm-header-eyebrow { font-family: "Cinzel", serif; font-size: 7.5px; letter-spacing: 3px; color: rgba(232,196,106,0.3); text-transform: uppercase; }
      .adv-rm-title { font-family: "Cinzel Decorative", cursive; font-size: 22px; font-weight: 900; background: linear-gradient(135deg, #fffaf0, var(--gold-bright), var(--gold-mid)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; line-height: 1.25; }
      .adv-rm-name-tag { font-family: "Cinzel", serif; font-size: 11px; letter-spacing: 1px; color: rgba(245,228,176,0.4); }
      .adv-rm-rule { display: flex; align-items: center; justify-content: center; height: 1px; margin: 0 40px; background: linear-gradient(90deg, transparent, rgba(201,151,58,0.4), transparent); position: relative; z-index: 1; }
      .adv-rm-rule-ornament { position: absolute; font-size: 12px; color: var(--gold-mid); background: var(--parchment); padding: 0 8px; line-height: 1; }

      /* ══ EDIT BODY ══ */
      .adv-edit-body { padding: 22px 28px 28px; position: relative; z-index: 1; }
      .adv-edit-section-label { font-family: "Cinzel", serif; font-size: 8px; font-weight: 700; letter-spacing: 2.5px; text-transform: uppercase; color: var(--gold-dim); margin-bottom: 18px; padding-bottom: 7px; border-bottom: 1px solid rgba(201,151,58,0.15); }
      .adv-edit-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; margin-bottom: 14px; }
      .adv-edit-grid .full-width { grid-column: span 2; }
      .adv-edit-field { display: flex; flex-direction: column; gap: 5px; }
      .adv-edit-label { font-family: "Cinzel", serif; font-size: 8.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .adv-req { color: #b52020; font-weight: 900; margin-left: 2px; }
      .adv-edit-wrap { display: flex; align-items: center; background: #fff9f0; border: 1px solid rgba(201,151,58,0.28); clip-path: polygon(10px 0%, calc(100% - 10px) 0%, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0% 50%); overflow: hidden; transition: border-color 0.3s, box-shadow 0.3s; }
      .adv-edit-wrap:focus-within { border-color: var(--gold-mid); background: #fffaf0; box-shadow: 0 0 0 3px rgba(201,151,58,0.12); }
      .adv-edit-wrap-readonly { background: #f0ece4; cursor: default; }
      .adv-edit-wrap-readonly:focus-within { box-shadow: none; border-color: rgba(201,151,58,0.28); }
      .adv-edit-prefix { font-family: "Cinzel", serif; font-size: 11px; font-weight: 600; color: var(--text-mid); padding: 13px 12px 13px 20px; border-right: 1px solid rgba(201,151,58,0.2); flex-shrink: 0; }
      .adv-edit-input { flex: 1; background: transparent; border: none; outline: none; font-family: "EB Garamond", serif; font-size: 15px; color: var(--text-dark); padding: 13px 14px; }
      .adv-edit-input::placeholder { color: rgba(74,26,8,0.25); font-style: italic; }
      .adv-readonly-val { flex: 1; font-family: "Cinzel", serif; font-size: 14px; font-weight: 700; color: var(--text-dark); padding: 13px 14px; }

      /* ══ PERSON BLOCK ══ */
      .adv-person-block { margin-bottom: 12px; padding: 16px; border: 1px solid rgba(201,151,58,0.18); background: rgba(201,151,58,0.03); clip-path: polygon(8px 0%, calc(100% - 8px) 0%, 100% 8px, 100% calc(100% - 8px), calc(100% - 8px) 100%, 8px 100%, 0 calc(100% - 8px), 0 8px); }
      .adv-person-block-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
      .adv-person-block-title { font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--gold-dim); }
      .adv-person-remove { display: inline-flex; align-items: center; gap: 5px; background: none; border: 1px solid rgba(139,26,26,0.25); color: rgba(139,26,26,0.7); font-family: "Cinzel", serif; font-size: 8px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; padding: 5px 10px; cursor: pointer; clip-path: polygon(6px 0%,calc(100% - 6px) 0%,100% 50%,calc(100% - 6px) 100%,6px 100%,0 50%); transition: all 0.2s; }
      .adv-person-remove:hover { background: rgba(139,26,26,0.07); color: #8b1a1a; border-color: rgba(139,26,26,0.45); }
      .adv-select-wrap { position: relative; }
      .adv-nakshathram-select { appearance: none; -webkit-appearance: none; width: 100%; background: transparent; cursor: pointer; padding-right: 28px !important; font-family: "Noto Serif Malayalam", "EB Garamond", serif !important; font-size: 14px !important; color: var(--text-dark); }
      .adv-nakshathram-select option { font-family: "Noto Serif Malayalam", sans-serif; font-size: 14px; color: #1a0404; background: #fffdf7; }
      .adv-select-arrow { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: var(--gold-mid); font-size: 13px; pointer-events: none; line-height: 1; }
      .adv-add-person-btn { width: 100%; display: flex; align-items: center; justify-content: center; gap: 8px; font-family: "Cinzel", serif; font-size: 9px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; background: none; border: 1px dashed rgba(201,151,58,0.35); color: var(--gold-dim); padding: 12px; cursor: pointer; transition: all 0.2s; margin-top: 4px; clip-path: polygon(8px 0%,calc(100% - 8px) 0%,100% 8px,100% calc(100% - 8px),calc(100% - 8px) 100%,8px 100%,0 calc(100% - 8px),0 8px); }
      .adv-add-person-btn:hover { background: rgba(201,151,58,0.05); border-color: rgba(201,151,58,0.55); color: var(--gold-mid); }

      /* ══ ACTIONS ══ */
      .adv-edit-actions { display: flex; gap: 10px; align-items: center; justify-content: flex-end; margin-top: 20px; padding-top: 18px; border-top: 1px solid rgba(201,151,58,0.12); }
      .adv-btn { display: inline-flex; align-items: center; gap: 7px; font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; border: none; padding: 10px 18px; cursor: pointer; clip-path: polygon(10px 0%,calc(100% - 10px) 0%,100% 50%,calc(100% - 10px) 100%,10px 100%,0 50%); transition: all 0.2s; border: 1px solid transparent; }
      .adv-btn:disabled { opacity: 0.38; cursor: not-allowed; }
      .adv-btn-ghost { background: none; font-weight: bold; border-color: rgba(149,27,27,0.5); color: rgb(74,26,8); }
      .adv-btn-ghost:hover { background: rgba(201,151,58,0.06); }
      .adv-edit-save-btn { display: inline-flex; align-items: center; gap: 8px; font-family: "Cinzel", serif; font-size: 10px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--crimson-deep); background: linear-gradient(135deg, #7a4e0c 0%, var(--gold-mid) 20%, var(--gold-bright) 40%, #fff8e7 55%, var(--gold-bright) 70%, var(--gold-mid) 85%, #6b4209 100%); border: none; padding: 13px 28px; cursor: pointer; clip-path: polygon(14px 0%, calc(100% - 14px) 0%, 100% 50%, calc(100% - 14px) 100%, 14px 100%, 0% 50%); box-shadow: 0 6px 24px rgba(201,151,58,0.3); transition: all 0.35s ease; position: relative; overflow: hidden; }
      .adv-edit-save-btn:hover { transform: translateY(-2px); box-shadow: 0 12px 32px rgba(201,151,58,0.5); filter: brightness(1.05); }
      .adv-edit-save-btn:disabled { opacity: 0.38; cursor: not-allowed; transform: none; box-shadow: none; }
      .adv-edit-saving-spinner { width: 12px; height: 12px; border-radius: 50%; border: 2px solid rgba(26,4,4,0.2); border-top-color: var(--crimson-deep); animation: spin 0.7s linear infinite; flex-shrink: 0; }
      @keyframes spin { to { transform: rotate(360deg); } }

      /* ══ TOAST ══ */
      .mb-toast-wrap { position: fixed; top: 28px; right: 28px; z-index: 4000; animation: mbToastIn 0.38s cubic-bezier(0.23,1,0.32,1) both; }
      @keyframes mbToastIn { from { opacity: 0; transform: translateX(60px); } to { opacity: 1; transform: none; } }
      .mb-toast { width: 340px; background: linear-gradient(145deg, #FDF3E3, #faf0dc, #f5e8cc); border: 1px solid var(--border); border-radius: 2px; box-shadow: 0 12px 32px rgba(26,4,4,0.14); padding: 0 0 14px; overflow: hidden; position: relative; }
      .mb-toast-bar { height: 3px; background: linear-gradient(90deg,transparent,var(--accent) 20%,var(--accent) 80%,transparent); margin-bottom: 16px; }
      .mb-tc { position: absolute; width: 9px; height: 9px; border-color: rgba(201,151,58,0.4); border-style: solid; }
      .mb-tc.tl { top: 11px; left: 11px; border-width: 1px 0 0 1px; }
      .mb-tc.tr { top: 11px; right: 11px; border-width: 1px 1px 0 0; }
      .mb-tc.bl { bottom: 7px; left: 11px; border-width: 0 0 1px 1px; }
      .mb-tc.br { bottom: 7px; right: 11px; border-width: 0 1px 1px 0; }
      .mb-toast-medal { position: absolute; left: 18px; top: 26px; width: 34px; height: 34px; border-radius: 50%; border: 1px solid; display: flex; align-items: center; justify-content: center; }
      .mb-toast-spin { position: absolute; inset: -3px; border-radius: 50%; border: 1px dashed rgba(201,151,58,0.28); animation: spin 8s linear infinite; }
      .mb-toast-body { padding: 0 40px 0 64px; }
      .mb-toast-row { display: flex; align-items: center; gap: 6px; margin-bottom: 5px; }
      .mb-toast-title { font-family: "Cinzel", serif; font-size: 9.5px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; flex: 1; }
      .mb-toast-rule { height: 1px; margin-bottom: 6px; }
      .mb-toast-msg { font-family: "EB Garamond", serif; font-style: italic; font-size: 14px; color: rgba(74,26,8,0.72); line-height: 1.5; }
      .mb-toast-foot { height: 1px; margin: 12px 18px 0; background: linear-gradient(90deg,transparent,rgba(201,151,58,0.22),transparent); }
      .mb-toast-x { position: absolute; top: 20px; right: 13px; background: none; border: none; font-size: 12px; cursor: pointer; opacity: 0.4; transition: opacity 0.2s; }
      .mb-toast-x:hover { opacity: 0.85; }

      /* ══ RESPONSIVE ══ */
      @media (max-width: 900px) {
        .adv-vazhipadu-grid { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 640px) {
        .mb-hero { padding: 28px 16px 0; }
        .mb-hero-title { font-size: 20px; }
        .mb-my-bookings-btn { clip-path: none; border-radius: 28px; }
        .adv-offerings-wrap { padding: 20px 14px 40px; }
        .adv-vazhipadu-grid { grid-template-columns: 1fr; gap: 14px; }
        .adv-modal-overlay { padding: 0; align-items: flex-end; }
        .adv-regal-modal { clip-path: none !important; border-radius: 22px 22px 0 0 !important; max-height: 93vh; border-bottom: none; width: 100%; max-width: 100% !important; }
        .adv-edit-body { padding: 16px 16px 28px; }
        .adv-edit-grid { grid-template-columns: 1fr; gap: 12px; }
        .adv-edit-grid .full-width { grid-column: span 1; }
        .adv-edit-wrap { clip-path: none; border-radius: 28px; }
        .adv-edit-save-btn { clip-path: none; border-radius: 28px; flex: 1; justify-content: center; }
        .adv-edit-actions .adv-btn { clip-path: none; border-radius: 28px; }
        .adv-add-person-btn { clip-path: none; border-radius: 28px; }
        .adv-person-block { clip-path: none; border-radius: 12px; }
        .adv-person-remove { clip-path: none; border-radius: 20px; }
        .adv-vazhipadu-card { clip-path: none; border-radius: 14px; }
        .adv-vz-book-btn { clip-path: none; border-radius: 20px; }
        .mb-toast-wrap { top: auto; bottom: 14px; right: 14px; left: 14px; }
        .mb-toast { width: 100%; clip-path: none; border-radius: 12px; }
      }
    `}</style>
  );
}
}